import { Box } from '@mui/material';
import { PropsWithChildren } from 'react';

import { useUserAgent } from '@/hooks/useUserAgent';
import { COLORS } from '@/styles/tokens/colors';

/**
 * Renders a styled keyboard key badge (gray background, rounded corners).
 * Use this for any keyboard shortcut labels (e.g. "Shift", "Enter").
 */
export function KeyboardShortcutKey({ children }: PropsWithChildren) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 0.75,
        py: 0.25,
        backgroundColor: COLORS.GRAY[200],
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: 1,
        minWidth: '28px',
      }}
    >
      {children}
    </Box>
  );
}

interface ModifierKeyProps {
  /**
   * When true, render only the text label ("Cmd" on Mac) without the
   * leading ⌘ glyph. Useful for compact contexts where horizontal space
   * is at a premium. Has no effect on non-Mac since "Ctrl" has no glyph.
   */
  hideIcon?: boolean;
}

/**
 * Renders the platform-appropriate modifier key label
 * ("⌘ Cmd" on Mac, "Ctrl" on Windows/Linux).
 */
export function ModifierKey({ hideIcon = false }: ModifierKeyProps = {}) {
  const { isMac } = useUserAgent();

  const macLabel = hideIcon ? 'Cmd' : '⌘ Cmd';
  return <KeyboardShortcutKey>{isMac ? macLabel : 'Ctrl'}</KeyboardShortcutKey>;
}
